import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Box,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { NavigateNext, Add } from '@mui/icons-material';
import AcademicQuestionPaperList from './AcademicQuestionPaperList';
import ConfirmDialog from './ConfirmDialog';
import { academicQuestionPaperApi } from './academicQuestionPaperApi';

const ListQuestionPapers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [questionPapers, setQuestionPapers] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchQuestionPapers();
  }, [page, rowsPerPage]);

  const fetchQuestionPapers = async (searchParams = {}) => {
    setLoading(true);
    try {
      let response;
      if (Object.keys(searchParams).length > 0) {
        response = await academicQuestionPaperApi.search({
          ...searchParams,
          page,
          size: rowsPerPage,
        });
      } else {
        response = await academicQuestionPaperApi.getPaged(page, rowsPerPage);
      }
      
      if (response.success && response.data) {
        setQuestionPapers(response.data.content || []);
        setTotalElements(response.data.totalElements || 0);
      } else {
        setError('Failed to load question papers');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load question papers';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (params) => {
    fetchQuestionPapers(params);
  };

  const handleView = (id) => {
    navigate(`/question-papers/view/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/question-papers/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await academicQuestionPaperApi.delete(deleteId);
      setSuccess('Question paper deleted successfully');
      fetchQuestionPapers();
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to delete question paper';
      setError(message);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleVerify = async (id) => {
    try {
      await academicQuestionPaperApi.verify(id);
      setSuccess('Question paper verified successfully');
      fetchQuestionPapers();
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to verify question paper';
      setError(message);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await academicQuestionPaperApi.toggleActive(id);
      setSuccess('Question paper status updated successfully');
      fetchQuestionPapers();
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to update status';
      setError(message);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link color="inherit" href="/">
          Home
        </Link>
        <Typography color="text.primary">Question Papers</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Question Papers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all academic question papers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/question-papers/add')}
        >
          New Question Paper
        </Button>
      </Box>

      <AcademicQuestionPaperList
        questionPapers={questionPapers}
        totalElements={totalElements}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onVerify={handleVerify}
        onToggleActive={handleToggleActive}
        onSearch={handleSearch}
        loading={loading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Question Paper"
        message="Are you sure you want to delete this question paper? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmColor="error"
      />

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ListQuestionPapers;